/**
 * Lightweight Charts v5 lifecycle.
 *
 * v5 API notes that matter here, because they changed from v4:
 *   * series are created with `chart.addSeries(CandlestickSeries, opts, pane)`
 *     -- there is no `addCandlestickSeries()` any more;
 *   * the third argument is a PANE INDEX, which is how the volume subplot
 *     gets its own row instead of being squashed under the candles;
 *   * markers come from the standalone `createSeriesMarkers(series, ...)`
 *     plugin rather than `series.setMarkers()`.
 */

import { useEffect, useRef, useState } from 'react'
import {
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createChart,
  createSeriesMarkers,
} from 'lightweight-charts'
import type {
  IChartApi,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  Time,
} from 'lightweight-charts'

import { COLORS, candleOptions, chartOptions } from '@/theme/chart'
import { ZonePrimitive } from './ZonePrimitive'

export interface ChartHandles {
  chart: IChartApi
  candles: ISeriesApi<'Candlestick'>
  volume: ISeriesApi<'Histogram'>
  markers: ISeriesMarkersPluginApi<Time>
  zones: ZonePrimitive
  lines: Map<string, ISeriesApi<'Line'>>
}

export function useChart(container: HTMLDivElement | null): ChartHandles | null {
  const [handles, setHandles] = useState<ChartHandles | null>(null)
  const handlesRef = useRef<ChartHandles | null>(null)

  useEffect(() => {
    if (!container) return

    const chart = createChart(container, {
      ...chartOptions,
      width: container.clientWidth,
      height: container.clientHeight,
    })

    const candles = chart.addSeries(CandlestickSeries, candleOptions)

    // Pane 1: volume. A separate pane keeps the price scale readable --
    // overlaying volume on price is what makes most home-made charts unusable.
    const volume = chart.addSeries(
      HistogramSeries,
      { priceFormat: { type: 'volume' }, priceScaleId: '' },
      1,
    )
    chart.panes()[1]?.setHeight(90)

    const markers = createSeriesMarkers(candles, [])

    const zones = new ZonePrimitive()
    candles.attachPrimitive(zones)

    const next: ChartHandles = { chart, candles, volume, markers, zones, lines: new Map() }
    handlesRef.current = next
    setHandles(next)

    const observer = new ResizeObserver(() => {
      chart.applyOptions({ width: container.clientWidth, height: container.clientHeight })
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
      chart.remove()
      handlesRef.current = null
      setHandles(null)
    }
  }, [container])

  return handles
}

/** Add or update one overlay line, creating the series on first use. */
export function upsertLine(
  handles: ChartHandles,
  key: string,
  points: { time: number; value: number }[],
  color: string,
  pane = 0,
): void {
  let series = handles.lines.get(key)
  if (!series) {
    series = handles.chart.addSeries(
      LineSeries,
      {
        color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      },
      pane,
    )
    handles.lines.set(key, series)
  }
  series.setData(points as never)
}

export function removeLine(handles: ChartHandles, key: string): void {
  const series = handles.lines.get(key)
  if (!series) return
  handles.chart.removeSeries(series)
  handles.lines.delete(key)
}

export function clearLines(handles: ChartHandles, keep: Set<string>): void {
  for (const key of [...handles.lines.keys()]) {
    if (!keep.has(key)) removeLine(handles, key)
  }
}

export const VOLUME_COLORS = {
  up: 'rgba(38, 166, 154, 0.5)',
  down: 'rgba(239, 83, 80, 0.5)',
} as const

export { COLORS }
